import { useEffect, useLayoutEffect, useRef } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import type { IssueBoardState } from '@aiai/shared';

/** The search param that deep-links a planet page's issue board to one state. */
export const ISSUES_PARAM = 'issues';

const BOARD_STATES: ReadonlySet<string> = new Set<IssueBoardState>([
  'all', 'proposed', 'open', 'building', 'review', 'failed', 'closed', 'mine',
]);

export function parseBoardState(value: string | null): IssueBoardState | undefined {
  return value !== null && BOARD_STATES.has(value) ? (value as IssueBoardState) : undefined;
}

/**
 * `?issues=<state>` on a planet page (Inbox click, a Needs-you link): every
 * navigation that carries it calls `onOpen` (show the Issues tab, scroll to it)
 * once the page is `ready`, and `panelKey` changes so the board remounts on that
 * filter — even when the URL was already the same (a repeat click).
 */
export function useBoardDeepLink(ready: boolean, onOpen: () => void) {
  const [searchParams] = useSearchParams();
  const { key } = useLocation();
  const status = parseBoardState(searchParams.get(ISSUES_PARAM));
  const onOpenRef = useRef(onOpen);
  useLayoutEffect(() => {
    onOpenRef.current = onOpen;
  });
  useEffect(() => {
    if (status && ready) onOpenRef.current();
  }, [status, key, ready]);
  return { status, panelKey: status ? `${status}:${key}` : 'all' };
}
